import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  BtnBold,
  BtnBulletList,
  BtnClearFormatting,
  BtnItalic,
  BtnNumberedList,
  BtnRedo,
  BtnStrikeThrough,
  BtnUnderline,
  BtnUndo,
  Editor,
  EditorProvider,
  Separator,
  Toolbar,
} from "react-simple-wysiwyg";
import VirtualList from "react-virtual-drag-list";

import SessionsApi from "../../../../../../../api/sessionApi";
import SpecialistApi from "../../../../../../../api/specialistsApi";
import { ReactComponent as Clock } from "../../../../../../../assets/SpecTools/clock.svg";
import { ReactComponent as Delete } from "../../../../../../../assets/SpecTools/delete.svg";
import { ReactComponent as MenuIcon } from "../../../../../../../assets/SpecTools/menu.svg";
import { ReactComponent as Refresh } from "../../../../../../../assets/SpecTools/refresh.svg";
import { ReactComponent as Stickynote } from "../../../../../../../assets/SpecTools/stickynote.svg";
import { ReactComponent as Upload } from "../../../../../../../assets/SpecTools/upload.svg";
import ConfirmationModal from "../../../../../../../components/ConfirmationModal/ConfirmationModal";
import Input from "../../../../../../../components/Input/Input";
import Loader from "../../../../../../../components/Loader/Loader";
import MultipleSelect from "../../../../../../../components/MultipleSelect/MultipleSelect";
import ProgressBar from "../../../../../../../components/ProgressBar/ProgressBar";
import StandartButton from "../../../../../../../components/StandartButton/StandartButton";
import { directions } from "../../../../../../../constans/directions";
import { videoExtensions } from "../../../../../../../constans/filesExtensions";
import {
  availableLanguages,
  ISO639Lang,
  ISO639LangReversed,
} from "../../../../../../../constans/languagesList";
import { ContextProvider } from "../../../../../../../contextProvider";
import { SessionPackage } from "../../../../../../../types/TypeSession";
import { SpecialistTagsDirections } from "../../../../../../../types/TypeSpecialist";
import Notify, { SuccesNotify } from "../../../../../../../utils/toaster";
import PackageSessionCard from "./PackageSessionCard/PackageSessionCard";
import s from "./PackagesTool.module.css";
import {
  hasMissingFields,
  onDrop,
  packageVideoUpload,
  truncateString,
  validateInput,
  validatePackageFields,
} from "./PackageUtils";

export type NewPackageType = {
  title: { text: string; language: string }[];
  description: { text: string; language: string }[];
  packageCost: { amount: number; language: string }[];
  tags: string[];
  isActive: boolean;
  specializations: string[];
  directions: string[];
  sessions: {
    id: number;
    title: { text: string; language: string }[];
    description?: { text: string; language: string }[];
    datesAndTime: {
      duration: number;
    }[];
    nextSessionGap: { min: number; max: number };
    specialization: SpecialistTagsDirections | string;
    order: number;
  }[];
  project?: string;
  defaultForRecommend: boolean;
};

type PackagesToolProps = {
  specialization: string;
  setCreatePackageModal: Dispatch<SetStateAction<boolean>>;
  setPackageData: Dispatch<SetStateAction<SessionPackage | undefined>>;
  projectId: string;
  packageId?: string;
};

const PackagesTool = ({
  specialization,
  setCreatePackageModal,
  setPackageData,
  projectId,
  packageId,
}: PackagesToolProps) => {
  const token = localStorage.getItem("token");
  const windowWidth = window.innerWidth;
  const { userData } = useContext(ContextProvider);
  const [specializationsData, setSpecializationsData] = useState<
    | {
        value: string;
        lable: string;
        specialization: SpecialistTagsDirections | undefined;
        mainSpecializationRef: string;
      }[]
    | null
  >(null);

  const [newPackageData, setNewPackageData] = useState<NewPackageType>({
    title: [],
    description: [],
    packageCost: [],
    isActive: false,
    tags: [],
    specializations: [],
    directions: [],
    sessions: [],
    defaultForRecommend: false,
    project: projectId,
  });

  const [newPackageFiles, setNewPackageFiles] = useState<
    { language: string; file: File }[] | null
  >(null);
  const [actionLoaders, setActionLoaders] = useState({ generalLoader: false });
  const [currentPackageFile, setCurrentPackageFile] = useState<{
    video:
      | { _id: string; name: string; link: string; language: string }[]
      | null;
  }>({ video: null });

  const [tagsDirection, setTagsDirection] = useState<
    SpecialistTagsDirections | undefined
  >();

  const [tags, setTags] = useState<
    { lable: string; value: string; parentTag?: string }[] | undefined
  >();
  const [langsData, setLangsData] = useState<{
    langsData: { value: string; label: string }[];
    selectLang: string | undefined;
  }>({
    langsData: [],
    selectLang: undefined,
  });
  const [availabelLangs, setAvailabelLangs] = useState(
    availableLanguages.map((item) => ISO639Lang[item]),
  );
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [videoUplaodProgress, setVideoUplaodProgress] = useState<{
    totalSize: number;
    uplaodedSize: number;
  }>();
  const [confirmDeleteLangModal, setConfirmDeleteLangModal] = useState(false);
  const [langToDelete, setLangToDelete] = useState<string | undefined>();
  const [showErrorFields, setShowErrorFields] = useState(false);

  const openPopUpMenu = () => {
    setIsPopupOpen((prev) => !prev);
  };

  useEffect(() => {
    const allLangs = availableLanguages.map((item) => ISO639Lang[item]);
    if (!langsData.langsData.length) {
      setAvailabelLangs(allLangs);
      setLangsData({
        langsData: allLangs.map((item, index) => ({
          value: ISO639LangReversed[item],
          label: item,
        })),
        selectLang: "en",
      });
      return;
    }

    const emtyTextFieldArray = langsData.langsData.map((item) => ({
      language: item,
      text: "",
    }));
    const currentTitle = emtyTextFieldArray.map((item) => ({
      text: newPackageData.title
        ? (newPackageData.title.find(
            (el) => el.language === item.language.value,
          )?.text ?? "")
        : "",
      language: item.language.value,
    }));
    const currentDescription = emtyTextFieldArray.map((item) => ({
      text: newPackageData.description
        ? (newPackageData.description.find(
            (el) => el.language === item.language.value,
          )?.text ?? "")
        : "",
      language: item.language.value,
    }));
    const currentSessions = newPackageData.sessions.map((session) => ({
      ...session,
      title: emtyTextFieldArray.map((item) => ({
        text: session.title
          ? (session.title.find((el) => el.language === item.language.value)
              ?.text ?? "")
          : "",
        language: item.language.value,
      })),
      description: emtyTextFieldArray.map((item) => ({
        text: session.description
          ? (session.description.find(
              (el) => el.language === item.language.value,
            )?.text ?? "")
          : "",
        language: item.language.value,
      })),
    }));
    setNewPackageData((prev) => ({
      ...prev,
      title: currentTitle,
      description: currentDescription,
      sessions: currentSessions,
    }));
    const newAvalabelLangs = allLangs.filter(
      (str) =>
        !langsData.langsData.some(
          (obj) => obj.value === ISO639LangReversed[str],
        ),
    );
    setAvailabelLangs(newAvalabelLangs);
  }, [langsData.langsData]);

  const packageStatusData = [
    { value: "true", lable: "Published" },
    { value: "false", lable: "Draft" },
  ];

  useEffect(() => {
    if (!specializationsData) return;
    setTagsDirection(directions[specialization]);

    const specializationId = specializationsData.find(
      (el) => el.specialization === directions[specialization],
    )?.value;

    setNewPackageData((prev) => ({
      ...prev,
      specializations: [specializationId!],
    }));
  }, [specialization, specializationsData]);

  useEffect(() => {
    if (
      !specializationsData ||
      !packageId ||
      !newPackageData.specializations.length
    )
      return;
    setTagsDirection(
      specializationsData.find(
        (el) => el.value === newPackageData.specializations[0],
      )!.specialization,
    );
  }, [packageId, specializationsData, newPackageData.specializations]);

  useEffect(() => {
    const makeAsync = async () => {
      if (!packageId || !token) return;
      const response = await SpecialistApi.getSpecialistSessionPackageById(
        token,
        packageId,
      );

      if (response.status && response.package) {
        const filesResponse = await SessionsApi.getPackageFiles(
          response.package._id,
        );

        if (
          filesResponse.status &&
          filesResponse.packageVideos &&
          filesResponse.packageVideos.length
        ) {
          setCurrentPackageFile((prev) => ({
            ...prev,
            video: filesResponse.packageVideos!.map((item) => ({
              _id: item._id,
              name: item.filename,
              link: item.link,
              language: item.language,
            })),
          }));
        }
      }

      if (response.status && response.package) {
        setLangsData({
          langsData: availableLanguages.map((item) => ({
            value: item,
            label: ISO639Lang[item],
          })),
          selectLang: response.package!.title[0].language,
        });

        const responsePackageData = {
          title: [...response.package.title],
          description: response.package.description
            ? [...response.package.description]
            : [],

          isActive: response.package.isActive,
          tags: response.package.tags ?? [],
          specializations: response.package.specializations
            ? response.package.specializations.map((item) => item._id)
            : [],
          sessions: response.package.sessions.map((item) => ({
            ...item,
            title: [...item.title],
            description: item.description ? [...item.description] : [],
            id: item.order,
          })),
          directions: response.package.directions.map((item) => item._id),
          project: response.package.project?._id,
          defaultForRecommend: response.package.defaultForRecommend,
        };

        //@ts-expect-error
        setNewPackageData(responsePackageData);
      }
    };
    makeAsync();
  }, [packageId]);

  const gatTags = async () => {
    if (!token || !userData || !tagsDirection) return;
    const payload = {
      level: 1,
      direction: tagsDirection,
    };
    const response = await SpecialistApi.getSpecialistTags(token, payload);

    if (response.status && response.tags) {
      const tagsData = response.tags.map((item) => ({
        lable: item.labels.find((el) => el.language === "en")!.text,
        value: item._id,
        parentTag: item.parentTag,
      }));
      setTags(tagsData);
    }
  };
  useEffect(() => {
    if (!tagsDirection) return;

    gatTags();
  }, [tagsDirection]);

  useEffect(() => {
    (async () => {
      if (!token) return;
      const response = await SpecialistApi.getAllSpecialzations(token);
      if (response.status && response.specializations) {
        const availabelSpecializations = response.specializations.filter(
          (el) => el.specialization !== specialization,
        );

        setSpecializationsData(
          availabelSpecializations.map((item) => ({
            value: item._id,
            lable: item.labels.find((el) => el.language === "en")?.text!,
            specialization: item.specialization as SpecialistTagsDirections,
            mainSpecializationRef: item.mainSpecializationRef,
          })),
        );
      }
    })();
  }, []);

  const handleVideoChange = (event: ChangeEvent<HTMLInputElement>) => {
    const fileInput = event.target;
    const file = event.target.files && event.target.files[0];

    if (!file) {
      setNewPackageFiles(null);
      return;
    }
    const videoSizeLimit = 1000 * 1024 * 1024; // 1000 MB

    if (file.size >= videoSizeLimit) {
      const error = "Some file size is more than 50 mb!";
      Notify(error as string);
      return;
    }
    setNewPackageFiles((prevVideos) => {
      if (!prevVideos) {
        return [{ language: langsData.selectLang!, file }];
      }

      const videoIndex = prevVideos.findIndex(
        (video) => video.language === langsData.selectLang!,
      );

      if (videoIndex !== -1) {
        const updatedVideos = [...prevVideos];
        updatedVideos[videoIndex].file = file;
        return updatedVideos;
      } else {
        return [...prevVideos, { language: langsData.selectLang!, file }];
      }
    });
    fileInput.value = "";
  };

  const getAllSessionDuration = () => {
    if (!newPackageData.sessions.length) return 0;
    const durationArr = newPackageData.sessions.map(
      (item) => item.datesAndTime[0].duration,
    );

    let result = durationArr.reduce((accumulator, currentValue) => {
      return accumulator + currentValue;
    }, 0);
    result = Number((result / 60).toFixed(1));
    return result;
  };

  const createNewPackage = async () => {
    if (!validatePackageFields(newPackageData)) {
      Notify("Fill in all required fields");
      setShowErrorFields(true);
      return;
    }
    if (!token || !specializationsData || !userData) return;
    const newPackage = {
      ...newPackageData,
      packageCost: newPackageData.packageCost,
    };
    const currentSelectSpecializationRef = specializationsData.find(
      (el) => el.value === newPackageData.specializations[0],
    )?.mainSpecializationRef;
    if (!currentSelectSpecializationRef) return;

    const payload = {
      newPackage: newPackage,
      userId: userData!._id,
    };

    setActionLoaders((prev) => ({ ...prev, generalLoader: true }));
    const response = await SessionsApi.createSessionPackage(token, payload);

    if (!response.status && response.message) {
      Notify(response.message);
    }
    if (response.status && response.package) {
      if (!newPackageFiles) {
        SuccesNotify("Package has been created");
        setNewPackageData({
          title: [],
          description: [],
          packageCost: [],
          isActive: true,
          tags: [],
          specializations: [],
          sessions: [],
          directions: [],
          defaultForRecommend: false,
        });
        setNewPackageFiles(null);
        setActionLoaders((prev) => ({ ...prev, generalLoader: false }));
        setPackageData(response.package);
        setCreatePackageModal(false);
        return;
      }
      if (newPackageFiles) {
        const filesData = newPackageFiles.map((item) => ({
          language: item.language,
          video: item.file,
        }));
        await setPackageFiles(response.package._id, filesData);
        setNewPackageFiles(null);
        setVideoUplaodProgress(undefined);
        setActionLoaders((prev) => ({ ...prev, generalLoader: false }));
        setPackageData(response.package);
        setCreatePackageModal(false);
      }
    }
    setActionLoaders((prev) => ({ ...prev, generalLoader: false }));
  };

  const updatePackage = async () => {
    if (!token || !userData || !packageId) return;
    const updateData = {
      ...newPackageData,
      sessions: newPackageData.sessions.map(({ id, ...rest }) => rest),
      _id: packageId,
    };

    if (!validatePackageFields(newPackageData)) {
      return Notify("Fill in all required fields");
    }
    setActionLoaders((prev) => ({ ...prev, generalLoader: true }));
    const response = await SessionsApi.updatePackage(token, updateData);

    if (!response.status && response.message) {
      Notify(response.message);
    }
    if (response.status && response.package) {
      if (!newPackageFiles) {
        SuccesNotify("Package has been updated");
        setNewPackageData({
          title: [],
          description: [],
          packageCost: [],
          isActive: true,
          tags: [],
          specializations: [],
          sessions: [],
          directions: [],
          defaultForRecommend: false,
        });
        setNewPackageFiles(null);
        setActionLoaders((prev) => ({ ...prev, generalLoader: false }));
        setPackageData(response.package);
        setCreatePackageModal(false);
        return;
      }
      if (newPackageFiles) {
        const filesData = newPackageFiles.map((item) => ({
          language: item.language,
          video: item.file,
        }));
        await setPackageFiles(response.package._id, filesData);
        setActionLoaders((prev) => ({ ...prev, generalLoader: false }));
        setPackageData(response.package);
        setCreatePackageModal(false);
        setNewPackageFiles(null);
      }
    }
  };

  const setPackageFiles = async (
    packageId: string,
    filesData: { language: string; video: File }[],
  ) => {
    if (!token) return;
    await packageVideoUpload(
      filesData,
      token,
      setVideoUplaodProgress,
      packageId,
    );
  };

  const deleteFile = async (
    fileId: string,
    fileType: "packageImage" | "packageVideo" | "packageSessionImage",
  ) => {
    if (!token || !packageId) return;
    const payload = {
      packageId: packageId,
      fileId: fileId,
      fileType: fileType,
    };

    const response = await SessionsApi.deletePackageSessionFile(token, payload);

    if (response.status && fileType === "packageVideo") {
      setCurrentPackageFile((prev) => ({ ...prev, video: null }));
    }
  };

  if (actionLoaders.generalLoader) {
    return (
      <div className={s.loaderContainer}>
        {videoUplaodProgress === undefined && <Loader />}
        {videoUplaodProgress !== undefined && (
          <ProgressBar
            progress={(() => {
              const progress = Math.round(
                (videoUplaodProgress.uplaodedSize /
                  videoUplaodProgress.totalSize) *
                  100,
              );
              return progress > 100 ? 100 : progress;
            })()}
            description={
              "The server processes your video. It will be available within 5 minutes."
            }
          />
        )}
      </div>
    );
  }

  return (
    <div className={s.container}>
      <div className={s.leftCotextBlock}>
        <div className={s.whiteBlock} style={{ paddingBottom: 0 }}>
          <span>
            {
              "Add a list of languages ​​to localize your session set to start filling in the text fields"
            }
          </span>
          <div className={s.langTabBlock}>
            {langsData.langsData.map((item, index) => (
              <div
                key={index}
                style={
                  showErrorFields &&
                  hasMissingFields(item.value, newPackageData)
                    ? { backgroundColor: "var(--red)" }
                    : {}
                }
                className={
                  langsData.selectLang === item.value
                    ? s.activeLangTab
                    : s.notActiveLangTab
                }
                onClick={() =>
                  setLangsData((prev) => ({
                    ...prev,
                    selectLang: item.value,
                  }))
                }
              >
                {item.label}
                {/*   <div
                  className={s.deleteLangBtn}
                  role={"button"}
                  onClick={() => {
                    setLangToDelete(item.value);
                    setConfirmDeleteLangModal(true);
                  }}
                >
                  <CloseIcon width={20} height={20} />
                </div> */}
              </div>
            ))}
            {isPopupOpen && (
              <div className={s.plusLangBtn} style={{ width: "auto" }}>
                <MultipleSelect
                  data={availabelLangs.map((lang) => ({
                    lable: lang,
                    value: ISO639LangReversed[lang],
                  }))}
                  setValue={(value) => {
                    setLangsData((prev) => ({
                      selectLang: prev.selectLang
                        ? prev.selectLang
                        : (value as string),
                      langsData: [
                        ...prev.langsData,

                        {
                          label: ISO639Lang[value as string],
                          value: value as string,
                        },
                      ],
                    }));
                    setIsPopupOpen(false);
                  }}
                  multiplie={false}
                  value={""}
                  lable={"Select language"}
                  border="1px solid #DADADA"
                  padding="10px 20px"
                  width="200px"
                />
              </div>
            )}
            {availabelLangs && availabelLangs.length > 0 && (
              <div
                onClick={!isPopupOpen ? openPopUpMenu : undefined}
                className={s.plusLangBtn}
                role={"button"}
                style={{ opacity: isPopupOpen ? 0.5 : 1 }}
              >
                +
              </div>
            )}
          </div>
        </div>

        <div className={s.whiteBlock}>
          <h2 className={s.blockHead}>Basic info</h2>
          <Input
            inputValue={
              newPackageData.title.find(
                (el) => el.language === langsData.selectLang,
              )?.text!
            }
            onChangeInput={(value) =>
              setNewPackageData((prev) => ({
                ...prev,
                title: prev.title.map((el) =>
                  el.language === langsData.selectLang
                    ? { ...el, text: value }
                    : el,
                ),
              }))
            }
            border={
              showErrorFields &&
              validateInput(langsData.selectLang, newPackageData.title)
                ? "1px solid red"
                : "1px solid #DADADA"
            }
            placeholder={"Title..."}
            lable={"Title"}
            isVisible
            disabled={!langsData.selectLang ? true : false}
          />
          <div className={s.descriptionBlock}>
            <span className={s.dataLabel}>
              Description
              <span className={s.requiredStar}>*</span>
            </span>
            <EditorProvider>
              <Editor
                value={
                  newPackageData.description.find(
                    (el) => el.language === langsData.selectLang,
                  )?.text
                }
                onChange={(e) => {
                  if (
                    e.target.value.length >= 600 ||
                    e.target.value.includes("<img src=")
                  )
                    return;
                  setNewPackageData((prev) => ({
                    ...prev,
                    description: prev.description.map((el) =>
                      el.language === langsData.selectLang
                        ? { ...el, text: e.target.value }
                        : el,
                    ),
                  }));
                }}
                containerProps={{
                  style: {
                    resize: "vertical",
                    borderRadius: "20px",
                    border:
                      showErrorFields &&
                      validateInput(
                        langsData.selectLang,
                        newPackageData.description,
                      )
                        ? "1px solid red"
                        : "1px solid #DADADA",
                  },
                }}
                disabled={!langsData.selectLang ? true : false}
              >
                <Toolbar
                  style={{
                    backgroundColor: "transparent",
                    borderBottom: "1px solid #DADADA",
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "5px 10px",
                    color: "white",
                  }}
                >
                  <div className={s.toolsBlock}>
                    {window.innerWidth > 768 && (
                      <>
                        <BtnUndo />
                        <BtnRedo />
                        <Separator />
                      </>
                    )}
                    <BtnBold />
                    <BtnItalic />
                    <BtnUnderline />
                    <BtnStrikeThrough />
                    <Separator />
                    <BtnNumberedList />
                    <BtnBulletList />
                    <BtnClearFormatting />
                    <Separator />
                    {/*  <BtnStyles /> */}
                  </div>
                  <span className={s.charactersLimit}>600 characters</span>
                </Toolbar>
              </Editor>
            </EditorProvider>
          </div>
        </div>
        <div className={s.whiteBlock}>
          <h2 className={s.blockHead}>Introduction video</h2>
          <div className={s.videoUploadBlock}>
            <div className={s.fileInputBlock}>
              {((newPackageFiles &&
                newPackageFiles.find(
                  (video) => video.language === langsData.selectLang,
                )) ||
                (currentPackageFile.video &&
                  currentPackageFile.video!.find(
                    (video) => video.language === langsData.selectLang,
                  )?.name)) && (
                <div className={s.uploadedVideoNameBlock}>
                  {newPackageFiles &&
                  newPackageFiles.find(
                    (video) => video.language === langsData.selectLang,
                  )
                    ? truncateString(
                        newPackageFiles.find(
                          (video) => video.language === langsData.selectLang,
                        )?.file.name!,
                        20,
                      )
                    : truncateString(
                        currentPackageFile.video!.find(
                          (video) => video.language === langsData.selectLang,
                        )?.name!,
                        20,
                      )}
                  <Delete
                    className={s.deleteIcon}
                    onClick={
                      currentPackageFile.video &&
                      currentPackageFile.video.find(
                        (video) => video.language === langsData.selectLang,
                      )
                        ? () =>
                            deleteFile(
                              currentPackageFile.video!.find(
                                (video) =>
                                  video.language === langsData.selectLang,
                              )!._id,
                              "packageVideo",
                            )
                        : () =>
                            setNewPackageFiles((prev) =>
                              prev!.filter(
                                (el) => el.language !== langsData.selectLang,
                              ),
                            )
                    }
                  />
                </div>
              )}
              <label>
                <input
                  style={{ display: "none" }}
                  type="file"
                  accept={videoExtensions.join(", ")}
                  onChange={(e) => handleVideoChange(e)}
                  multiple={false}
                  disabled={langsData.selectLang ? false : true}
                />
                {(newPackageFiles &&
                  newPackageFiles.find(
                    (video) => video.language === langsData.selectLang,
                  )) ||
                (currentPackageFile.video &&
                  truncateString(
                    currentPackageFile.video!.find(
                      (video) => video.language === langsData.selectLang,
                    )?.name ?? "",
                    20,
                  )) ? (
                  <div className={s.refreshFileBtn} role="button">
                    <Refresh className={s.refreshIcon} />
                    {windowWidth > 768 ? "Click to change" : ""}
                  </div>
                ) : (
                  <div
                    className={
                      langsData.selectLang
                        ? s.uplaodFileBtn
                        : s.disbaledFileChangeBtn
                    }
                  >
                    <Upload />
                    Upload video
                  </div>
                )}
              </label>
            </div>
          </div>
        </div>
        <div
          className={s.whiteBlock}
          style={{
            border: `1px solid ${
              !newPackageData.sessions.length && showErrorFields
                ? "red"
                : "var(--border)"
            }`,
          }}
        >
          <div className={s.packageSessionsHeadBlock}>
            <h2 className={s.blockHead}>
              Package sessions
              <span className={s.requiredStar}>*</span>
            </h2>
            <div className={s.generalSessionsInfoBlock}>
              <div className={s.sessionInfoTitleBlock}>
                <div className={s.generalSessionInfoElement}>
                  <Stickynote className={s.sessionsInfoIcon} />
                  {newPackageData.sessions.length + " sessions"}
                </div>
                <div className={s.generalSessionInfoElement}>
                  <Clock className={s.sessionsInfoIcon} />
                  {getAllSessionDuration() + " hours total duration"}
                </div>
              </div>
              {newPackageData.sessions.length < 100 && (
                <div
                  role="button"
                  className={s.addSectionBtn}
                  onClick={() =>
                    setNewPackageData((prev) => ({
                      ...prev,
                      sessions: [
                        ...prev.sessions,
                        {
                          id: newPackageData.sessions.length + 1,
                          title: langsData.langsData.map((item) => ({
                            language: item.value,
                            text: "",
                          })),
                          description: langsData.langsData.map((item) => ({
                            language: item.value,
                            text: "",
                          })),
                          datesAndTime: [
                            {
                              duration: 60,
                            },
                          ],
                          order: newPackageData.sessions.length + 1,
                          nextSessionGap: prev.sessions.length
                            ? {
                                min: prev.sessions[prev.sessions.length - 1]
                                  .nextSessionGap.min,
                                max: prev.sessions[prev.sessions.length - 1]
                                  .nextSessionGap.max,
                              }
                            : { min: 1, max: 7 },
                          specialization: tagsDirection ? tagsDirection : "",
                        },
                      ],
                    }))
                  }
                >
                  Add new section
                </div>
              )}
            </div>
          </div>
          <VirtualList
            className="virtual-list"
            dataKey="id"
            dataSource={newPackageData.sessions}
            handle="#handle-drag"
            onDrop={(e) => onDrop(e, setNewPackageData)}
            keeps={100}
          >
            {(record: {
              id: number;
              title: {
                language: string;
                text: string;
              }[];
              description?: {
                language: string;
                text: string;
              }[];
              datesAndTime: {
                duration: number;
              }[];
              order: number;
              nextSessionGap: { min: number; max: number };
              specialization: SpecialistTagsDirections | string;
            }) => {
              return (
                <div className={s.cardBlock}>
                  <MenuIcon id="handle-drag" className={s.menuIcon} />
                  <PackageSessionCard
                    session={record}
                    setSessionState={setNewPackageData}
                    selectLang={langsData.selectLang}
                    showErrorFields={showErrorFields}
                    validateInput={validateInput}
                  />
                </div>
              );
            }}
          </VirtualList>
        </div>
      </div>
      <div className={s.rightContentBlock}>
        <div className={`${s.whiteBlock} ${s.publishBlock}`}>
          <div className={s.mainActionsBlock}>
            <StandartButton
              action={!packageId ? createNewPackage : updatePackage}
              buttonTitle={"Save"}
              width="100%"
            />
          </div>
        </div>
        <div className={s.whiteBlock}>
          <h2 className={s.blockHead}>Package status</h2>
          <div>
            {/*  <span>{strings.productStatus}</span> */}
            <MultipleSelect
              data={packageStatusData}
              multiplie={false}
              setValue={(value) =>
                setNewPackageData((prev) => ({
                  ...prev,
                  isActive: value === "false" ? false : true,
                }))
              }
              value={newPackageData.isActive.toString()}
              border="1px solid #DADADA"
              padding="10px 20px"
            />
          </div>
        </div>
        <div className={s.whiteBlock}>
          <h2 className={s.blockHead}>Categorias</h2>

          <div className={s.specializationsBlock}>
            <span>
              Direction
              <span className={s.requiredStar}>*</span>
            </span>
            <MultipleSelect
              data={tags ?? []}
              multiplie={true}
              setValue={(value) => {
                setNewPackageData((prev) => ({
                  ...prev,
                  directions: value as string[],
                }));
              }}
              value={newPackageData.directions}
              border={
                showErrorFields &&
                (!newPackageData.directions ||
                  !newPackageData.directions.length)
                  ? "1px solid red"
                  : "1px solid #DADADA"
              }
              padding="10px 20px"
              disabled={!tags || !tags.length ? true : false}
              chip
            />
          </div>
        </div>
      </div>

      <div className={s.mobileSaveBtn}>
        <StandartButton
          action={!packageId ? createNewPackage : updatePackage}
          buttonTitle={packageId ? "Save changes" : "Publish"}
          width="100%"
        />
      </div>
      <ConfirmationModal
        confirmTitle={
          "All data filled in this language will be lost, are you sure you want to delete this language?"
        }
        modalVisible={confirmDeleteLangModal}
        setModalVisible={setConfirmDeleteLangModal}
        confirmFunc={() => {
          setLangsData((prev) => ({
            ...prev,
            langsData: prev.langsData.filter(
              (lang) => lang.value !== langToDelete,
            ),
            selectLang:
              langToDelete === prev.selectLang
                ? prev.langsData.filter(
                    (lang) => lang.value !== langToDelete,
                  )[0]
                  ? prev.langsData.filter(
                      (lang) => lang.value !== langToDelete,
                    )[0].value
                  : undefined
                : prev.selectLang,
          }));
          setConfirmDeleteLangModal(false);
          setLangToDelete(undefined);
        }}
      />
    </div>
  );
};

export default PackagesTool;
